import { fetchHotspots, Hotspot, hotspotsAs_GEO_JSON, hotspotTypeLabel } from './hotspots-data';
import { loadMapboxAssets } from './mapbox';

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		mapboxgl: any;
	}
}

class MrHotspotsMap extends HTMLElement {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	#map: any | null = null;

	#valueChangeHandler = () => {
		const selectedFilter = this.querySelector( 'input:checked' );

		if ( !selectedFilter ) return;

		const filterBy = selectedFilter.getAttribute( 'value' );

		if ( filterBy && filterBy !== 'everything' ) {
			this.#map?.setFilter(
				'labels',
				[
					'all',
					[
						'==',
						'type',
						filterBy,
					],
				]
			);

			this.#map?.setFilter(
				'points',
				[
					'all',
					[
						'==',
						'type',
						filterBy,
					],
				]
			);

			this.querySelectorAll( '.mapboxgl-popup' ).forEach( ( popup ) => {
				popup.remove();
			} );

			return;
		}

		this.#map?.setFilter(
			'labels'
		);

		this.#map?.setFilter(
			'points'
		);
	};

	connectedCallback() {
		this.#init();

		this.addEventListener( 'change', this.#valueChangeHandler );
	}

	disconnectedCallback() {
		this.#map?.remove();
		this.#map = null;
		this.innerHTML = '';

		this.removeEventListener( 'change', this.#valueChangeHandler );
	}

	async #init() {
		const [
			,
			hotspots,
			logo,
		] = await Promise.all( [
			loadMapboxAssets(),
			fetchHotspots(),
			new Promise<HTMLImageElement|void>( ( resolve ) => {
				const img = new Image( 70, 70 );

				img.onload = () => {
					resolve( img );
				};

				img.onerror = () => {
					resolve();
				};

				img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent( '<svg viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"><path class="favicon" d="M477.873 252.371C477.873 252.371 477.476 252.371 477.278 252.371C477.08 252.371 476.98 252.133 476.98 251.895C476.98 251.657 477.179 251.538 477.278 251.419C477.476 251.419 477.675 251.419 477.873 251.419C497.814 249.753 509.718 243.923 509.718 223.576V207.87C509.718 179.432 486.405 179.432 450.393 179.432H4.26584C1.88491 179.432 0 181.693 0 184.548V327.809C0 330.664 1.88491 332.925 4.26584 332.925H452.675C488.687 332.925 512 332.925 512 304.487V280.571C512 259.391 499.202 254.037 477.774 252.609L477.873 252.371ZM358.43 291.756C358.43 307.105 345.831 307.105 326.386 307.105H157.836C155.455 307.105 153.57 304.844 153.57 301.988V269.743C153.57 266.887 155.455 264.627 157.836 264.627H326.386C345.831 264.627 358.43 264.627 358.43 279.976V291.756ZM358.43 223.695C358.43 239.044 345.831 239.044 326.386 239.044H157.836C155.455 239.044 153.57 236.784 153.57 233.928V210.012C153.57 207.156 155.455 204.895 157.836 204.895H326.386C345.831 204.895 358.43 204.895 358.43 220.244V223.695Z" fill="#000"/><path class="favicon" d="M507.734 358.388H362.696C360.315 358.388 358.43 360.649 358.43 363.505V452.983C358.43 456.671 355.354 459.051 352.577 457.742C286.506 424.783 220.534 391.823 154.463 358.864C153.967 358.626 153.372 358.507 152.777 358.507H4.26584C1.88491 358.507 0 360.768 0 363.624V506.884C0 509.739 1.88491 512 4.26584 512H149.304C151.685 512 153.57 509.739 153.57 506.884V417.406C153.57 413.717 156.646 411.337 159.423 412.646C225.494 445.605 291.466 478.565 357.537 511.524C358.033 511.762 358.628 511.881 359.223 511.881H507.734C510.115 511.881 512 509.62 512 506.765V363.505C512 360.649 510.115 358.388 507.734 358.388Z" fill="#000"/><path class="favicon" d="M507.734 0H362.696C360.315 0 358.43 2.26075 358.43 5.11643V112.561C358.43 127.911 345.831 127.911 326.386 127.911H185.614C166.169 127.911 153.57 127.911 153.57 112.561V5.11643C153.57 2.26075 151.685 0 149.304 0H4.26584C1.88491 0 0 2.26075 0 5.11643V119.344C0 153.493 28.0752 153.493 71.2296 153.493H440.77C484.024 153.493 512 153.493 512 119.344V5.11643C512 2.26075 510.115 0 507.734 0Z" fill="#000"/></svg>' );
			} ),
		] );

		const filter = this.#renderHotspotsFilter( hotspots );
		if ( filter ) {
			this.appendChild( filter );
		}

		const mapContainer = document.createElement( 'div' );
		mapContainer.className = 'hotspots-map__map-container';
		this.appendChild( mapContainer );

		// XXX_SECRET
		window.mapboxgl.accessToken = 'pk.eyJ1IjoibXJoZW5yeSIsImEiOiJjamhqMGVka3UwaWt1MzBrZHFoNmJxanMxIn0.WFtYFcw3d7lbYCsXaQ_X1A';

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.#map = await new Promise<any>( ( resolve ) => {
			const map = new window.mapboxgl.Map( {
				container: mapContainer,
				style: 'mapbox://styles/mapbox/light-v9',
				center: [
					4.40,
					51.2160,
				],
				zoom: 12.5,
				cooperativeGestures: true,
			} );

			map.on( 'load', () => {
				return resolve( map );
			} );
		} );

		const geoJSON = hotspotsAs_GEO_JSON( hotspots );

		this.#map.addSource( 'hotspots', {
			type: 'geojson',
			data: geoJSON,
		} );

		this.#map.addLayer( {
			id: 'points',
			type: 'circle',
			source: 'hotspots',
			paint: {
				'circle-color': [
					'case',
					[
						'boolean',
						[
							'feature-state',
							'hover',
						],
						false,
					],
					'#000',
					'#fff',
				],
				'circle-radius': 20,
			},
		} );

		this.#map.addLayer( {
			id: 'labels',
			source: 'hotspots',
			interactive: false,
			type: 'symbol',
			layout: {
				'text-field': '{label}',
				'text-size': 18,
				'text-font': [
					'Arial Unicode MS Bold',
				],
			},
			paint: {
				'text-color': [
					'case',
					[
						'boolean',
						[
							'feature-state',
							'hover',
						],
						false,
					],
					'#fff',
					'#000',
				],
			},
		} );

		if ( logo ) {
			this.#map.addImage( 'logo', logo, {
				pixelRatio: 2,
			} );

			this.#map.addLayer( {
				id: 'offficalHQ',
				source: {
					type: 'geojson',
					data: {
						type: 'Feature',
						geometry: {
							type: 'Point',
							coordinates: [
								4.4021,
								51.2335,
							],
						},
						properties: {},
					},
				},
				type: 'symbol',
				layout: {
					'icon-image': 'logo',
				},
			} );
		}

		this.#map.on( 'click', [
			'labels',
			'points',
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		], ( e: any ) => {
			if ( !this.#map ) {
				return;
			}

			if ( !e.features ) {
				return;
			}

			const firstFeature = e.features[0];
			if ( !firstFeature ) {
				return;
			}

			if ( !firstFeature.properties?.popup ) {
				return;
			}

			const geometry = firstFeature.geometry;
			if ( !geometry || geometry.type !== 'Point' ) {
				return;
			}

			const [
				lng,
				lat,
			] = geometry.coordinates;

			new window.mapboxgl.Popup()
				.setLngLat( [
					lng,
					lat,
				] )
				.setHTML( firstFeature.properties.popup )
				.setOffset( 18 )
				.setMaxWidth( 'var(--hotspots-map-popup-max-width, 240px)' )
				.addTo( this.#map );
		} );

		// When the user moves their mouse over the state-fill layer, we'll update the
		// feature state for the feature under the mouse.
		this.#map.on( 'mousemove', [
			'labels',
			'points',
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		], ( e: any ) => {
			if ( !e.features?.length ) {
				return;
			}

			this.#unsetHover();

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			e.features.forEach( ( feature: any ) => {
				if ( !feature.id ) {
					return;
				}

				this.#map?.setFeatureState(
					{
						source: 'hotspots',
						id: feature.id,
					},
					{
						hover: true,
					}
				);
			} );
		} );

		this.#map.on( 'mouseleave', [
			'labels',
			'points',
		], () => {
			this.#unsetHover();
		} );
	}

	#unsetHover() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.#map?.querySourceFeatures( 'hotspots' ).forEach( ( feature: any ) => {
			this.#map?.setFeatureState(
				{
					source: 'hotspots',
					id: feature.id,
				},
				{
					hover: false,
				}
			);
		} );
	}

	#renderHotspotsFilter( hotspots: Array<Hotspot> ): HTMLElement|undefined {
		const fieldset = document.createElement( 'FIELDSET' );
		fieldset.className = 'hotspots-list__filter';

		{
			const legend = document.createElement( 'LEGEND' );
			legend.className = 'u-visually-hidden';
			legend.innerText = 'Filter by';

			fieldset.appendChild( legend );
		}

		{
			const uniqueOptions = new Set<string>( [
				'everything',
			] );

			for ( let i = 0; i < hotspots.length; i++ ) {
				if ( hotspots[i].type ) uniqueOptions.add( hotspots[i].type! );
			}

			if ( uniqueOptions.size === 0 ) return;

			const options = Array.from( uniqueOptions );

			const radioGroup = document.createElement( 'DIV' );
			radioGroup.className = 'hotspots-list__filter__radio-group';

			for ( let i = 0; i < options.length; i++ ) {
				const option = options[i];

				const radioWithLabel = document.createElement( 'DIV' );
				radioWithLabel.className = 'hotspots-list__filter__radio-with-label type-b';

				{
					const input = document.createElement( 'INPUT' ) as HTMLInputElement;
					input.setAttribute( 'type', 'radio' );
					input.setAttribute( 'id', `hotspots-filter--${option}` );
					input.setAttribute( 'name', 'hotspots-filter' );
					input.setAttribute( 'value', option );
					input.checked = option === 'everything';

					radioWithLabel.appendChild( input );
				}

				{
					const label = document.createElement( 'LABEL' );
					label.className = 'type-b';
					label.setAttribute( 'for', `hotspots-filter--${option}` );
					label.innerText = hotspotTypeLabel( option );

					radioWithLabel.appendChild( label );
				}

				radioGroup.appendChild( radioWithLabel );
			}

			fieldset.appendChild( radioGroup );
		}

		return fieldset;
	}
}

window.customElements.define( 'mr-hotspots-map', MrHotspotsMap );
